import { useEffect, useState } from "react";

import { isSSR } from "./helpers";

/** Nearest ancestor that actually scrolls, or null if the window scrolls. */
const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

/**
 * Tracks which document heading the reader is currently on and returns its id,
 * so a table of contents can highlight the matching entry as the page scrolls.
 *
 * Watches every `[id]` heading inside `containerSelector` and treats the lowest
 * heading whose top has passed the reading line (a band just under the fixed
 * docs header) as active. Using "last heading scrolled past" rather than
 * "currently intersecting" keeps a section highlighted even when it is taller
 * than the viewport and no heading is on screen.
 *
 * The docs layout scrolls an inner container rather than the window, so the
 * scroll listener is attached to the headings' real scroll parent when there is
 * one, falling back to the window.
 */
const useActiveHeading = (containerSelector: string): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (isSSR) return;

    const container = document.querySelector<HTMLElement>(containerSelector);
    if (!container) return;

    const headings = Array.from(
      container.querySelectorAll<HTMLElement>(
        "h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]"
      )
    );
    if (headings.length === 0) return;

    // Reading line just below the fixed docs header (DOCS_HEADER_HEIGHT is 4rem).
    const READING_LINE = 120;

    const update = () => {
      let current = headings[0].id;
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= READING_LINE) {
          current = heading.id;
        } else {
          break;
        }
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };

    const scrollParent = getScrollParent(headings[0]);
    const target: HTMLElement | Window = scrollParent ?? window;

    update();
    target.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });

    return () => {
      target.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [containerSelector]);

  return activeId;
};

export default useActiveHeading;
